import React, { useMemo, useState } from "react";
import {
  Box,
  Chip,
  Collapse,
  Grid,
  IconButton,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import { CreditScoreDonut } from "./CreditScoreDonut";
import { formatCurrency } from "@poseidon/utils";
import {
  SummaryTable,
  SummaryTableRow,
} from "@poseidon/ui/components";

// --- Helpers ---

function safeFormatCurrency(value) {
  if (value == null || value === -1) return "-";
  return formatCurrency(value);
}

function formatDate(dateStr) {
  if (!dateStr) return "-";
  try {
    return new Date(dateStr).toLocaleDateString("en-GB");
  } catch {
    return dateStr;
  }
}

function groupAccountsByStatus(accounts) {
  const statusOrder = ["Active", "Settled", "Default"];
  const grouped = {};

  for (const account of accounts) {
    const status = account.paymentStatus || "Unknown";
    if (!grouped[status]) grouped[status] = [];
    grouped[status].push(account);
  }

  return statusOrder
    .filter((s) => grouped[s])
    .map((status) => ({ status, accounts: grouped[status] }))
    .concat(
      Object.keys(grouped)
        .filter((s) => !statusOrder.includes(s))
        .map((status) => ({ status, accounts: grouped[status] }))
    );
}

function groupAccountsByType(accounts) {
  const grouped = {};
  for (const account of accounts) {
    const type = account.accountType || "Other";
    if (!grouped[type]) grouped[type] = [];
    grouped[type].push(account);
  }
  return Object.entries(grouped).map(([type, accs]) => ({
    type,
    accounts: accs,
  }));
}

function summariseGroup(accounts) {
  let balance = 0;
  let limit = 0;
  let payment = 0;
  let missedPayments = 0;
  let defaultAmount = 0;
  const companies = new Set();
  const startDates = new Set();
  const endDates = new Set();

  for (const a of accounts) {
    balance += a.currentBalance || 0;
    limit += a.creditLimitBalance || 0;
    payment += a.paymentAmount || 0;
    missedPayments +=
      a.missedPayments?.twelveMonths ?? a["missedPayments.twelveMonths"] ?? 0;
    defaultAmount += a.defaultBalance || 0;
    if (a.companyName) companies.add(a.companyName);
    if (a.startDate) startDates.add(a.startDate);
    if (a.endDate) endDates.add(a.endDate);
  }

  return {
    count: accounts.length,
    balance,
    limit,
    payment,
    missedPayments,
    defaultAmount,
    company: companies.size === 1 ? [...companies][0] : "Various",
    startDate: startDates.size === 1 ? [...startDates][0] : "Various",
    endDate: endDates.size === 1 ? [...endDates][0] : "Various",
  };
}

function formatPaymentWithPct(payment, limit) {
  if (!payment) return safeFormatCurrency(0);
  if (limit && limit > 0) {
    const pct = Math.round((payment / limit) * 100);
    return `${safeFormatCurrency(payment)} (${pct}%)`;
  }
  return safeFormatCurrency(payment);
}

// Column widths shared across all table levels to keep alignment
const COL = {
  expand: 40,
  status: 80,
  type: 140,
  number: 60,
  balance: 90,
  limit: 90,
  payment: 110,
  company: 120,
  startDate: 90,
  endDate: 90,
  missed: 80,
  default: 100,
};

// --- Sub-components ---

function InfoCard({ label, value, color }) {
  return (
    <Paper
      variant="outlined"
      sx={{
        p: 1.5,
        textAlign: "center",
        bgcolor: color || "transparent",
        height: "100%",
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
      }}
    >
      <Typography variant="caption" color="text.secondary" gutterBottom>
        {label}
      </Typography>
      <Typography variant="h6" sx={{ fontWeight: 600 }}>
        {value}
      </Typography>
    </Paper>
  );
}

function StatusChip({ status }) {
  const colorMap = {
    Active: "success",
    Settled: "default",
    Default: "error",
  };
  return (
    <Chip
      label={status}
      color={colorMap[status] || "default"}
      size="small"
      variant="outlined"
    />
  );
}

function AccountsTableRow({ statusGroup, isExpanded, onToggle }) {
  const { status, accounts } = statusGroup;
  const summary = summariseGroup(accounts);
  const typeGroups = groupAccountsByType(accounts);
  const canExpand = typeGroups.length > 1;

  return (
    <>
      <TableRow
        sx={{
          "& > *": {
            borderBottom: canExpand && isExpanded ? "none" : undefined,
          },
          bgcolor: isExpanded ? "action.hover" : undefined,
        }}
      >
        <TableCell sx={{ width: COL.expand, p: 0.5 }}>
          {canExpand && (
            <IconButton size="small" onClick={onToggle}>
              {isExpanded ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
            </IconButton>
          )}
        </TableCell>
        <TableCell sx={{ width: COL.status }}>
          <StatusChip status={status} />
        </TableCell>
        <TableCell sx={{ width: COL.type }}>
          {summary.count > 1 ? "Various" : typeGroups[0]?.type || "-"}
        </TableCell>
        <TableCell sx={{ width: COL.number }} align="right">
          {summary.count}
        </TableCell>
        <TableCell sx={{ width: COL.balance }} align="right">
          {safeFormatCurrency(summary.balance)}
        </TableCell>
        <TableCell sx={{ width: COL.limit }} align="right">
          {summary.limit ? safeFormatCurrency(summary.limit) : "-"}
        </TableCell>
        <TableCell sx={{ width: COL.payment }} align="right">
          {safeFormatCurrency(summary.payment)}
        </TableCell>
        <TableCell sx={{ width: COL.company }}>{summary.company}</TableCell>
        <TableCell sx={{ width: COL.startDate }}>
          {summary.startDate === "Various"
            ? "Various"
            : formatDate(summary.startDate)}
        </TableCell>
        <TableCell sx={{ width: COL.endDate }}>
          {summary.endDate === "Various"
            ? "Various"
            : formatDate(summary.endDate)}
        </TableCell>
        <TableCell sx={{ width: COL.missed }} align="right">
          {summary.missedPayments}
        </TableCell>
        <TableCell sx={{ width: COL.default }} align="right">
          {safeFormatCurrency(summary.defaultAmount)}
        </TableCell>
      </TableRow>
      {canExpand && (
        <TableRow>
          <TableCell sx={{ p: 0 }} colSpan={12}>
            <Collapse in={isExpanded} timeout="auto" unmountOnExit>
              <Table size="small" sx={{ tableLayout: "fixed" }}>
                <TableBody>
                  {typeGroups.map(({ type, accounts: typeAccounts }) => {
                    const typeSummary = summariseGroup(typeAccounts);
                    const canExpandType = typeAccounts.length > 1;
                    return (
                      <ExpandableTypeRow
                        key={type}
                        type={type}
                        accounts={typeAccounts}
                        summary={typeSummary}
                        canExpand={canExpandType}
                      />
                    );
                  })}
                </TableBody>
              </Table>
            </Collapse>
          </TableCell>
        </TableRow>
      )}
    </>
  );
}

function ExpandableTypeRow({ type, accounts, summary, canExpand }) {
  const [expanded, setExpanded] = useState(false);

  return (
    <>
      <TableRow
        sx={{
          bgcolor: "action.hover",
          "& > *": { borderBottom: canExpand && expanded ? "none" : undefined },
        }}
      >
        <TableCell sx={{ width: COL.expand, p: 0.5, pl: 4 }}>
          {canExpand && (
            <IconButton size="small" onClick={() => setExpanded(!expanded)}>
              {expanded ? (
                <KeyboardArrowUpIcon fontSize="small" />
              ) : (
                <KeyboardArrowDownIcon fontSize="small" />
              )}
            </IconButton>
          )}
        </TableCell>
        <TableCell sx={{ width: COL.status }} />
        <TableCell sx={{ width: COL.type }}>
          <Typography variant="body2">{type}</Typography>
        </TableCell>
        <TableCell sx={{ width: COL.number }} align="right">
          {summary.count}
        </TableCell>
        <TableCell sx={{ width: COL.balance }} align="right">
          {safeFormatCurrency(summary.balance)}
        </TableCell>
        <TableCell sx={{ width: COL.limit }} align="right">
          {summary.limit ? safeFormatCurrency(summary.limit) : "-"}
        </TableCell>
        <TableCell sx={{ width: COL.payment }} align="right">
          {type === "Credit card/Store card"
            ? formatPaymentWithPct(summary.payment, summary.limit)
            : safeFormatCurrency(summary.payment)}
        </TableCell>
        <TableCell sx={{ width: COL.company }}>{summary.company}</TableCell>
        <TableCell sx={{ width: COL.startDate }}>
          {summary.startDate === "Various"
            ? "Various"
            : formatDate(summary.startDate)}
        </TableCell>
        <TableCell sx={{ width: COL.endDate }}>
          {summary.endDate === "Various"
            ? "Various"
            : formatDate(summary.endDate)}
        </TableCell>
        <TableCell sx={{ width: COL.missed }} align="right">
          {summary.missedPayments}
        </TableCell>
        <TableCell sx={{ width: COL.default }} align="right">
          {safeFormatCurrency(summary.defaultAmount)}
        </TableCell>
      </TableRow>
      {canExpand && (
        <TableRow>
          <TableCell sx={{ p: 0 }} colSpan={12}>
            <Collapse in={expanded} timeout="auto" unmountOnExit>
              <Table size="small" sx={{ tableLayout: "fixed" }}>
                <TableBody>
                  {accounts.map((account) => (
                    <TableRow key={account.id} sx={{ bgcolor: "grey.50" }}>
                      <TableCell sx={{ width: COL.expand, p: 0.5, pl: 7 }} />
                      <TableCell sx={{ width: COL.status }} />
                      <TableCell sx={{ width: COL.type }}>
                        <Typography variant="caption">
                          {account.accountType}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ width: COL.number }} align="right">
                        1
                      </TableCell>
                      <TableCell sx={{ width: COL.balance }} align="right">
                        {safeFormatCurrency(account.currentBalance)}
                      </TableCell>
                      <TableCell sx={{ width: COL.limit }} align="right">
                        {account.creditLimitBalance
                          ? safeFormatCurrency(account.creditLimitBalance)
                          : "-"}
                      </TableCell>
                      <TableCell sx={{ width: COL.payment }} align="right">
                        {safeFormatCurrency(account.paymentAmount)}
                      </TableCell>
                      <TableCell sx={{ width: COL.company }}>
                        {account.companyName || "-"}
                      </TableCell>
                      <TableCell sx={{ width: COL.startDate }}>
                        {formatDate(account.startDate)}
                      </TableCell>
                      <TableCell sx={{ width: COL.endDate }}>
                        {formatDate(account.endDate)}
                      </TableCell>
                      <TableCell sx={{ width: COL.missed }} align="right">
                        {account.missedPayments?.twelveMonths ??
                          account["missedPayments.twelveMonths"] ??
                          0}
                      </TableCell>
                      <TableCell sx={{ width: COL.default }} align="right">
                        {safeFormatCurrency(account.defaultBalance)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Collapse>
          </TableCell>
        </TableRow>
      )}
    </>
  );
}

function AccountsSummaryTable({ accounts }) {
  const statusGroups = useMemo(
    () => groupAccountsByStatus(accounts),
    [accounts]
  );
  const [expandedStatus, setExpandedStatus] = useState(null);

  if (!accounts?.length) {
    return (
      <Typography variant="caption" color="text.secondary">
        No accounts found
      </Typography>
    );
  }

  return (
    <Box sx={{ overflowX: "auto" }}>
      <Table size="small" sx={{ tableLayout: "fixed" }}>
        <TableHead>
          <TableRow
            sx={{
              "& th": {
                fontWeight: 700,
                fontSize: "0.75rem",
              },
            }}
          >
            <TableCell sx={{ width: COL.expand }} />
            <TableCell sx={{ width: COL.status }}>Status</TableCell>
            <TableCell sx={{ width: COL.type }}>Type</TableCell>
            <TableCell sx={{ width: COL.number }} align="right">
              Number
            </TableCell>
            <TableCell sx={{ width: COL.balance }} align="right">
              Balance
            </TableCell>
            <TableCell sx={{ width: COL.limit }} align="right">
              Limit
            </TableCell>
            <TableCell sx={{ width: COL.payment }} align="right">
              Payment
            </TableCell>
            <TableCell sx={{ width: COL.company }}>Company</TableCell>
            <TableCell sx={{ width: COL.startDate }}>Start Date</TableCell>
            <TableCell sx={{ width: COL.endDate }}>End Date</TableCell>
            <TableCell sx={{ width: COL.missed }} align="right">
              Missed Payments
            </TableCell>
            <TableCell sx={{ width: COL.default }} align="right">
              Default Amount
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {statusGroups.map((group) => (
            <AccountsTableRow
              key={group.status}
              statusGroup={group}
              isExpanded={expandedStatus === group.status}
              onToggle={() =>
                setExpandedStatus(
                  expandedStatus === group.status ? null : group.status
                )
              }
            />
          ))}
        </TableBody>
      </Table>
    </Box>
  );
}

function TotalsCards({ accounts, raw }) {
  const mortgages = accounts.filter(
    (a) => a.accountType === "Mortgage" && a.paymentStatus === "Active"
  );
  const loansHp = accounts.filter(
    (a) =>
      a.paymentStatus === "Active" &&
      [
        "Unsecured loan (personal loans etc)",
        "Hire purchase/Conditional sale",
        "Balloon HP",
        "Fixed term deferred payment",
      ].includes(a.accountType)
  );
  const creditCards = accounts.filter(
    (a) =>
      a.paymentStatus === "Active" &&
      ["Credit card/Store card"].includes(a.accountType)
  );

  const mortgageSummary = summariseGroup(mortgages);
  const loansSummary = summariseGroup(loansHp);
  const ccSummary = summariseGroup(creditCards);

  return (
    <Grid container spacing={2}>
      <Grid item xs={12} sm={6} md={3}>
        <Paper variant="outlined" sx={{ p: 2 }}>
          <Stack spacing={1}>
            <Typography variant="subtitle2" fontWeight={700}>
              Total Mortgages
            </Typography>
            <SummaryTable>
              <SummaryTableRow label="No of accounts">
                {mortgageSummary.count}
              </SummaryTableRow>
              <SummaryTableRow label="Balance">
                {safeFormatCurrency(mortgageSummary.balance)}
              </SummaryTableRow>
              <SummaryTableRow label="Payments">
                {safeFormatCurrency(mortgageSummary.payment)}
              </SummaryTableRow>
              <SummaryTableRow label="Missed Payments">
                {mortgageSummary.missedPayments}
              </SummaryTableRow>
            </SummaryTable>
          </Stack>
        </Paper>
      </Grid>
      <Grid item xs={12} sm={6} md={3}>
        <Paper variant="outlined" sx={{ p: 2 }}>
          <Stack spacing={1}>
            <Typography variant="subtitle2" fontWeight={700}>
              Total Loans/HP
            </Typography>
            <SummaryTable>
              <SummaryTableRow label="No of accounts">
                {loansSummary.count}
              </SummaryTableRow>
              <SummaryTableRow label="Balance">
                {safeFormatCurrency(loansSummary.balance)}
              </SummaryTableRow>
              <SummaryTableRow label="Payments">
                {safeFormatCurrency(loansSummary.payment)}
              </SummaryTableRow>
              <SummaryTableRow label="Missed Payments">
                {loansSummary.missedPayments}
              </SummaryTableRow>
            </SummaryTable>
          </Stack>
        </Paper>
      </Grid>
      <Grid item xs={12} sm={6} md={3}>
        <Paper variant="outlined" sx={{ p: 2 }}>
          <Stack spacing={1}>
            <Typography variant="subtitle2" fontWeight={700}>
              Total Credit Cards
            </Typography>
            <SummaryTable>
              <SummaryTableRow label="No of accounts">
                {ccSummary.count}
              </SummaryTableRow>
              <SummaryTableRow label="Balance">
                {safeFormatCurrency(ccSummary.balance)}
              </SummaryTableRow>
              <SummaryTableRow label="Limits">
                {safeFormatCurrency(ccSummary.limit)}
              </SummaryTableRow>
              <SummaryTableRow label="Usage">
                {ccSummary.limit
                  ? `${Math.round(
                      (ccSummary.balance / ccSummary.limit) * 100
                    )}%`
                  : "-"}
              </SummaryTableRow>
              <SummaryTableRow label="Missed Payments">
                {ccSummary.missedPayments}
              </SummaryTableRow>
            </SummaryTable>
          </Stack>
        </Paper>
      </Grid>
      <Grid item xs={12} sm={6} md={3}>
        <Paper variant="outlined" sx={{ p: 2 }}>
          <Stack spacing={1}>
            <Typography variant="subtitle2" fontWeight={700}>
              Defaults
            </Typography>
            <DefaultsTable accounts={accounts} />
          </Stack>
        </Paper>
      </Grid>
    </Grid>
  );
}

function DefaultsTable({ accounts }) {
  const defaultAccounts = accounts.filter(
    (a) =>
      a.paymentStatus === "Default" ||
      (a.defaultBalance && a.defaultBalance > 0)
  );

  if (!defaultAccounts.length) {
    return (
      <Typography variant="caption" color="text.secondary">
        No defaults
      </Typography>
    );
  }

  return (
    <Table size="small">
      <TableHead>
        <TableRow>
          <TableCell sx={{ fontSize: "0.7rem", p: 0.5 }}>Type</TableCell>
          <TableCell sx={{ fontSize: "0.7rem", p: 0.5 }}>Lender</TableCell>
          <TableCell sx={{ fontSize: "0.7rem", p: 0.5 }} align="right">
            Amount
          </TableCell>
          <TableCell sx={{ fontSize: "0.7rem", p: 0.5 }}>Start Date</TableCell>
          <TableCell sx={{ fontSize: "0.7rem", p: 0.5 }}>End Date</TableCell>
          <TableCell sx={{ fontSize: "0.7rem", p: 0.5 }}>
            Date Satisfied
          </TableCell>
          <TableCell sx={{ fontSize: "0.7rem", p: 0.5 }} align="right">
            Current Balance
          </TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {defaultAccounts.map((a) => (
          <TableRow key={a.id}>
            <TableCell sx={{ fontSize: "0.75rem", p: 0.5 }}>
              {a.accountType}
            </TableCell>
            <TableCell sx={{ fontSize: "0.75rem", p: 0.5 }}>
              {a.companyName || "-"}
            </TableCell>
            <TableCell sx={{ fontSize: "0.75rem", p: 0.5 }} align="right">
              {safeFormatCurrency(a.defaultBalance)}
            </TableCell>
            <TableCell sx={{ fontSize: "0.75rem", p: 0.5 }}>
              {formatDate(a.startDate)}
            </TableCell>
            <TableCell sx={{ fontSize: "0.75rem", p: 0.5 }}>
              {formatDate(a.endDate)}
            </TableCell>
            <TableCell sx={{ fontSize: "0.75rem", p: 0.5 }}>
              {formatDate(a.defaultSatisfyDate)}
            </TableCell>
            <TableCell sx={{ fontSize: "0.75rem", p: 0.5 }} align="right">
              {safeFormatCurrency(a.currentBalance)}
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}

function CcjBankruptcySection({ raw }) {
  const hasCcj = raw?.publicInfoRecordsCount > 0;
  const hasBankruptcy =
    raw?.bankruptcy ||
    raw?.ivaDetected ||
    raw?.outstandingBankruptcyOrIvaDetected;

  return (
    <Paper variant="outlined" sx={{ p: 2 }}>
      <Stack spacing={1}>
        <Typography variant="subtitle2" fontWeight={700}>
          CCJ/Bankruptcy/IVA
        </Typography>
        {!hasCcj && !hasBankruptcy ? (
          <Typography variant="caption" color="text.secondary">
            No CCJ, Bankruptcy, or IVA records found
          </Typography>
        ) : (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontSize: "0.7rem" }}>Data Source</TableCell>
                <TableCell sx={{ fontSize: "0.7rem" }}>Trading Name</TableCell>
                <TableCell sx={{ fontSize: "0.7rem" }}>Status</TableCell>
                <TableCell sx={{ fontSize: "0.7rem" }} align="right">
                  Amount
                </TableCell>
                <TableCell sx={{ fontSize: "0.7rem" }}>Info Date</TableCell>
                <TableCell sx={{ fontSize: "0.7rem" }}>
                  Date Satisfied
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {hasCcj && (
                <TableRow>
                  <TableCell sx={{ fontSize: "0.75rem" }}>
                    Public Record
                  </TableCell>
                  <TableCell sx={{ fontSize: "0.75rem" }}>-</TableCell>
                  <TableCell sx={{ fontSize: "0.75rem" }}>
                    {raw?.outstandingPublicInfoRecordsCount > 0
                      ? "Outstanding"
                      : "Satisfied"}
                  </TableCell>
                  <TableCell sx={{ fontSize: "0.75rem" }} align="right">
                    {safeFormatCurrency(raw?.publicInfoRecordsValue?.amount)}
                  </TableCell>
                  <TableCell sx={{ fontSize: "0.75rem" }}>-</TableCell>
                  <TableCell sx={{ fontSize: "0.75rem" }}>-</TableCell>
                </TableRow>
              )}
              {raw?.bankruptcy && (
                <TableRow>
                  <TableCell sx={{ fontSize: "0.75rem" }}>Bankruptcy</TableCell>
                  <TableCell sx={{ fontSize: "0.75rem" }} colSpan={5}>
                    Detected
                  </TableCell>
                </TableRow>
              )}
              {raw?.ivaDetected && (
                <TableRow>
                  <TableCell sx={{ fontSize: "0.75rem" }}>IVA</TableCell>
                  <TableCell sx={{ fontSize: "0.75rem" }} colSpan={5}>
                    Detected
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        )}
      </Stack>
    </Paper>
  );
}

// --- Main Component ---

export const CreditCheckNew = ({ creditCheck }) => {
  const raw = creditCheck?.creditSearch?.raw ?? {};
  const mapped = creditCheck?.creditSearch?.mapped ?? {};
  const accounts = raw.creditAccounts ?? [];

  return (
    <Stack spacing={3}>
      {/* Row 1: Credit Score + Info Cards */}
      <Grid container spacing={2} alignItems="stretch">
        <Grid item xs={12} md={3}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              height: "100%",
            }}
          >
            <CreditScoreDonut title="Credit Score" value={mapped.creditScore} />
          </Box>
        </Grid>
        <Grid item xs={12} md={9}>
          <Grid container spacing={1.5}>
            <Grid item xs={6} sm={4} md={3}>
              <InfoCard
                label="Time on ER"
                value={`${mapped.timeOnElectoralRoleInYears ?? "-"} Years`}
              />
            </Grid>
            <Grid item xs={6} sm={4} md={3}>
              <InfoCard
                label="CCJ/Bankruptcy?"
                value={mapped.hasCcjOrBankruptcy ? "Yes" : "No"}
              />
            </Grid>
            <Grid item xs={6} sm={4} md={3}>
              <InfoCard
                label="No of Search past 12m"
                value={mapped.numberOfSearchesInLast12Months ?? 0}
              />
            </Grid>
            <Grid item xs={6} sm={4} md={3}>
              <InfoCard
                label="No Of Default Accounts"
                value={mapped.numberOfDefaultedAccounts ?? 0}
              />
            </Grid>
            <Grid item xs={6} sm={4} md={3}>
              <InfoCard
                label="CIFAS?"
                value={mapped.cifasDetected ? "Yes" : "No"}
              />
            </Grid>
            <Grid item xs={6} sm={4} md={3}>
              <InfoCard
                label="Default Amount"
                value={safeFormatCurrency(mapped.defaultedBalance)}
              />
            </Grid>
            <Grid item xs={12} sm={4} md={6}>
              <Paper variant="outlined" sx={{ p: 1.5, height: "100%" }}>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  gutterBottom
                  component="div"
                  textAlign="center"
                >
                  Total Missed Payments
                </Typography>
                <Stack
                  direction="row"
                  spacing={2}
                  justifyContent="center"
                  sx={{ mt: 0.5 }}
                >
                  {[
                    { label: "6m", value: raw.missedPayments?.sixMonths },
                    { label: "12m", value: raw.missedPayments?.twelveMonths },
                    {
                      label: "24m",
                      value: raw.missedPayments?.twentyFourMonths,
                    },
                    {
                      label: "36m",
                      value: raw.missedPayments?.thirtySixMonths,
                    },
                    {
                      label: "72m",
                      value: raw.missedPayments?.seventyTwoMonths,
                    },
                  ].map(({ label, value }) => (
                    <Stack key={label} alignItems="center" spacing={0}>
                      <Typography variant="caption" color="text.secondary">
                        {label}
                      </Typography>
                      <Typography variant="body2" fontWeight={600}>
                        {value ?? 0}
                      </Typography>
                    </Stack>
                  ))}
                </Stack>
              </Paper>
            </Grid>
          </Grid>
        </Grid>
      </Grid>

      {/* Row 2: Expandable Accounts Table */}
      <Paper variant="outlined" sx={{ p: 0, overflow: "hidden" }}>
        <AccountsSummaryTable accounts={accounts} />
      </Paper>

      {/* Row 3: Totals Cards */}
      <Box sx={{ overflow: "hidden" }}>
        <TotalsCards accounts={accounts} raw={raw} />
      </Box>

      {/* Row 4: CCJ/Bankruptcy */}
      <CcjBankruptcySection raw={raw} />
    </Stack>
  );
};
